"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { Search } from "lucide-react";
import { DualTabToggle } from "@/components/shared/dual-tab-toggle";
import { Skeleton } from "@/components/ui/skeleton";

type GlossaryTerm = {
  id: string;
  term: string;
  slug: string;
  category: string;
  definitionFormal: string;
  definitionSimple: string;
  example: string | null;
  relatedTerms: string[];
};

const CATEGORIES = ["All", "General", "MutualFunds", "Stocks", "Tax", "Bonds"];
const CATEGORY_LABELS: Record<string, string> = {
  All: "All",
  General: "General",
  MutualFunds: "Mutual Funds",
  Stocks: "Stocks",
  Tax: "Tax",
  Bonds: "Bonds",
};

export function GlossaryClient() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const activeCategory = searchParams.get("category") ?? "All";

  const [terms, setTerms] = useState<GlossaryTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"financial" | "simple">("simple");
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchTerms = useCallback(async (category: string, search: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== "All") params.set("category", category);
      if (search.trim()) params.set("search", search.trim());
      params.set("limit", "100");

      const res = await fetch(`/api/glossary?${params}`);
      const json = await res.json();
      if (json.success) {
        setTerms(json.data);
      }
    } catch {
      // Glossary fetch failed
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTerms(activeCategory, searchQuery);
  }, [activeCategory, fetchTerms, searchQuery]);

  function handleSearch(value: string) {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchTerms(activeCategory, value);
    }, 300);
  }

  // Group terms alphabetically
  const grouped = terms.reduce<Record<string, GlossaryTerm[]>>((acc, term) => {
    const letter = term.term[0]?.toUpperCase() ?? "#";
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(term);
    return acc;
  }, {});
  const sortedLetters = Object.keys(grouped).sort();

  return (
    <>
      {/* Search + tab toggle */}
      <div className="flex items-center gap-4 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search terms..."
            className="w-full bg-surface border border-surface-light/30 rounded-lg pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-dim focus:outline-none focus:border-blue/50 transition"
          />
        </div>
        <DualTabToggle activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-5" style={{ scrollbarWidth: "none" }}>
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => {
              const params = new URLSearchParams(searchParams.toString());
              if (cat === "All") {
                params.delete("category");
              } else {
                params.set("category", cat);
              }
              router.push(`/glossary?${params}`);
            }}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition cursor-pointer border-none whitespace-nowrap ${
              activeCategory === cat
                ? "bg-blue text-white"
                : "bg-navy-mid text-muted-foreground hover:text-foreground"
            }`}
          >
            {CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      {/* Terms list */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-xl" />
          ))}
        </div>
      ) : terms.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-muted-foreground text-sm">
            No terms found{searchQuery ? ` for "${searchQuery}"` : ""}.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedLetters.map((letter) => (
            <div key={letter}>
              <div className="text-blue text-sm font-bold mb-2 border-b border-surface-light/20 pb-1">
                {letter}
              </div>
              <div className="space-y-1">
                {grouped[letter].map((term) => {
                  const isExpanded = expandedSlug === term.slug;
                  const definition =
                    activeTab === "simple"
                      ? term.definitionSimple
                      : term.definitionFormal;

                  return (
                    <div
                      key={term.id}
                      className={`bg-surface border rounded-xl overflow-hidden transition ${
                        isExpanded ? "border-blue/40" : "border-surface-light/30"
                      }`}
                    >
                      <button
                        onClick={() =>
                          setExpandedSlug(isExpanded ? null : term.slug)
                        }
                        className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer border-none bg-transparent hover:bg-surface-light/5 transition"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-foreground text-sm font-semibold">
                            {term.term}
                          </span>
                          <span className="bg-surface-light/30 text-dim text-[10px] px-1.5 py-0.5 rounded-full">
                            {CATEGORY_LABELS[term.category] ?? term.category}
                          </span>
                        </div>
                        <span
                          className={`text-dim text-lg transition-transform duration-200 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        >
                          ▾
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="px-4 pb-4">
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {definition}
                          </p>
                          {term.example && (
                            <div className="mt-3 bg-navy-mid/50 rounded-lg p-3">
                              <span className="text-dim text-[10px] uppercase tracking-wide font-semibold block mb-1">
                                Example
                              </span>
                              <p className="text-muted-foreground text-xs leading-relaxed">
                                {term.example}
                              </p>
                            </div>
                          )}
                          {term.relatedTerms.length > 0 && (
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <span className="text-dim text-[10px]">Related:</span>
                              {term.relatedTerms.map((rt) => (
                                <span
                                  key={rt}
                                  className="text-blue text-xs cursor-pointer hover:underline"
                                  onClick={() => {
                                    setExpandedSlug(rt);
                                    setSearchQuery(rt.replace(/-/g, " "));
                                  }}
                                >
                                  {rt.replace(/-/g, " ")}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
